import { useState } from "react";
import { useMutation } from "react-query";
import { toast, ToastContainer } from "react-toastify";

import { updateUser } from "~/api/users/updateUser";
import { useAuth } from "~/contexts/providers/auth";
import { useLanguage } from "~/contexts/providers/language";
import englishTranslations from "~/localization/en";
import frenchTranslations from "~/localization/fr";
import {
  PaymentFormFields,
  PersonalInformationFormFields,
} from "~/types/account/account.types";
import { User } from "~/types/auth/auth.types";
import { SubscriptionStatus } from "~/types/subscription/subscription.types";
import { emailRegex, passwordRegex } from "~/utils/constants/regex";
import { isEmptyObject } from "~/utils/functions/general.functions";

import {
  paymentFieldsets,
  paymentFieldsetsEn,
  personalInformationFieldsets,
  personalInformationFieldsetsFr,
} from "./fieldSets";
import AccountMembershipForm from "./form";
import styles from "./index.module.scss";
import AccountLoginSection from "./loginSection";
import PaymentModal from "./paymentModal";
import SubscriptionForm from "./subscriptionForm";
import SubscriptionRenewal from "./subscriptionRenewal";

const clearEmptyFields = <T extends object>(data: T) => {
  for (const key in data) {
    const val = data[key];
    if (!val) delete data[key];
  }
};

const clearUserUnchangedFields = <T extends object>(
  data: T,
  user: User | undefined
) => {
  if (!user) return;
  for (const key in data) {
    if (key in user) {
      const defVal = `${user[key as unknown as keyof User]}`;
      const val = `${data[key]}`;
      if (val === defVal) delete data[key];
    }
  }
};

const AccountMembership = () => {
  const [personalInformationErrorField, setPersonalInformationErrorField] =
    useState<keyof PersonalInformationFormFields>();
  const [paymentErrorField, setPaymentErrorField] = useState();
  const [modalOpen, setModalOpen] = useState(false);

  const { user, setUser } = useAuth();

  const { language } = useLanguage();
  const accountTexts =
    language === "En"
      ? englishTranslations.accountPage
      : frenchTranslations.accountPage;

  const successNotification = () =>
    toast.success("Votre abonnement a bien été résilié", {
      position: "top-center",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });

  const userHasSubscription = !!user && !!user.subscription;

  const { isLoading: isUpdateUserLoading, mutate: updateUserMutation } =
    useMutation(updateUser, {
      onSuccess: (res) => {
        setUser(res);
      },
    });

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleEmptyPersonalInformationErrorField = () => {
    setPersonalInformationErrorField(undefined);
  };

  const handleEmptysetPaymentErrorField = () => {
    setPaymentErrorField(undefined);
  };

  const onSubmitPersonalInformation = (data: PersonalInformationFormFields) => {
    clearEmptyFields(data);
    clearUserUnchangedFields(data, user);
    if (isEmptyObject(data)) return;
    if (data.email && !emailRegex.test(data.email)) {
      setPersonalInformationErrorField("email");
      return;
    }
    if (data.password) {
      if (!passwordRegex.test(data.password)) {
        setPersonalInformationErrorField("password");
        return;
      }
      if (data.password !== data.confirmPassword) {
        setPersonalInformationErrorField("confirmPassword");
        return;
      }
    }
    updateUserMutation(data);
  };

  const onSubmitPayment = () => {
    setModalOpen(true);
  };

  return (
    <section>
      {user?.subscription?.renew === false && (
        <SubscriptionRenewal
          renew={false}
          successNotification={successNotification}
        />
      )}
      <SubscriptionForm />
      {userHasSubscription && (
        <AccountMembershipForm<PaymentFormFields>
          title={accountTexts.payment}
          fieldSets={language === "En" ? paymentFieldsetsEn : paymentFieldsets}
          errorField={paymentErrorField}
          error={
            user.stripeCardStatus === "REFUSED"
              ? "Moyen de paiement refusé"
              : undefined
          }
          defaultValues={{
            cardNumber: `**** **** **** ${user?.stripeLast4Digit ?? "****"}`,
          }}
          onSubmit={onSubmitPayment}
          handleEmptyErrorField={handleEmptysetPaymentErrorField}
        />
      )}
      <AccountLoginSection />
      <AccountMembershipForm<PersonalInformationFormFields>
        title={accountTexts.personalInformation}
        fieldSets={
          language === "Fr"
            ? personalInformationFieldsetsFr
            : personalInformationFieldsets
        }
        errorField={personalInformationErrorField}
        defaultValues={user}
        isLoading={isUpdateUserLoading}
        submitButtonText={
          user?.subscription?.subscriptionStatus === SubscriptionStatus.ACTIVE
            ? "Sauvegarder"
            : "Activer mon abonnement"
        }
        onSubmit={onSubmitPersonalInformation}
        handleEmptyErrorField={handleEmptyPersonalInformationErrorField}
        login={true}
      />

      <p className={styles.modifyOtherInformation}>
        {accountTexts.toChangeInformation}→{" "}
        <a href="mailto:contact@lesilencio.com">contact@lesilencio.com</a>
      </p>
      {user?.subscription?.renew && (
        <SubscriptionRenewal
          renew={true}
          successNotification={successNotification}
        />
      )}
      <PaymentModal open={modalOpen} handleClose={handleCloseModal} />
      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </section>
  );
};

export default AccountMembership;
